/**
 * One request in detail (B4): amounts, reason, the steps with who decided, the events, and a button for each action
 * the API allows (`allowedActions`). An action opens a small form in the dialog: the comment, required for an approval
 * on behalf (E5) and for an overruling (E10, E33), optional otherwise. The answer of the API replaces the request.
 */
import { useEffect, useId, useState } from 'react';
import type { CreditLimitRequestDto } from '../../../types/creditLimits';
import type { RequestActionInput } from './useCreditLimitRequests';
import { OverviewDialog } from '../overview/OverviewDialog';
import { nameFromEmail, ROLE_LABEL } from './chainPreview';
import {
    MAX_COMMENT_LENGTH, actionButtons, apiErrorText, commentError, formatChfExact, formatDateTime, statusText, type ActionButton,
} from './requestRules';

interface Props {
    request: CreditLimitRequestDto;
    onAct: (id: number, body: RequestActionInput) => Promise<CreditLimitRequestDto>;
    onClose: () => void;
    /** The button to open with (a click in the grid's action column). */
    initialAction?: ActionButton | null;
    /** `config.roles.finance`: only Finance sees the NAV error text (Gate B security review). */
    isFinance?: boolean;
    /** NAV writing is locked (`navWriteLockedAt` or `navAuthFailedAt`, Finance only). */
    navLocked?: boolean;
}

const DECISION: Record<string, string> = { pending: 'open', approved: 'approved', rejected: 'rejected' };

const NAV_MODE: Record<string, string> = { odata: 'Written by the app', manual: 'Entered in NAV by hand' };

/**
 * The NAV state of a request (C2): who wrote it and when, the value read back, whether the nightly run confirmed it.
 * The error text of a failed write is for Finance only.
 */
function NavBlock({ request, isFinance }: { request: CreditLimitRequestDto; isFinance: boolean }) {
    const { nav, status } = request;
    const started = status === 'ready' || status === 'writing' || status === 'written' || status === 'manual';
    if (!started && !nav.mode) return null;
    const showError = isFinance && !!nav.error;
    const done = status === 'written' || status === 'manual';
    return (
        <>
            <h3 style={{ margin: '16px 0 6px', fontSize: 'var(--fs-body)', fontWeight: 640, color: 'var(--text-strong)' }}>NAV</h3>
            <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', margin: 0 }}>
                <dt className="muted">State</dt>
                <dd style={{ margin: 0 }}>
                    {status === 'ready' && 'Not written yet.'}
                    {status === 'writing' && 'A write was started and has not reported back. Use «Check NAV» to read the value in NAV.'}
                    {status === 'written' && 'Written to NAV'}
                    {status === 'manual' && 'Entered in NAV by hand'}
                    {done && (nav.confirmedByNightlyRun ? ', confirmed by the nightly run.' : ', not yet confirmed by the nightly run.')}
                </dd>
                {nav.mode && (<><dt className="muted">How</dt><dd style={{ margin: 0 }}>{NAV_MODE[nav.mode]}</dd></>)}
                {nav.writtenBy && (<><dt className="muted">By</dt><dd style={{ margin: 0 }} title={nav.writtenBy}>{nameFromEmail(nav.writtenBy)}, {formatDateTime(nav.writtenAt)}</dd></>)}
                {nav.readBackChf !== null && (<><dt className="muted">Read back CHF</dt><dd style={{ margin: 0 }} className="tnum">{formatChfExact(nav.readBackChf)}</dd></>)}
                {nav.dwhLimitChf !== null && (<><dt className="muted">Nightly run CHF</dt><dd style={{ margin: 0 }} className="tnum">{formatChfExact(nav.dwhLimitChf)}</dd></>)}
            </dl>
            {showError && <div className="banner error" role="alert" style={{ marginTop: 8 }}><p>NAV error: {nav.error}</p></div>}
        </>
    );
}

const EVENT: Record<string, string> = {
    submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected', overruled: 'Overruled', withdrawn: 'Withdrawn',
    ready: 'Ready for NAV', nav_write_started: 'NAV write started', nav_write_ok: 'Written to NAV',
    nav_write_failed: 'NAV write failed', manual: 'Entered in NAV by hand',
};

export function RequestDialog({ request, onAct, onClose, initialAction = null, isFinance = false, navLocked = false }: Props) {
    const [chosen, setChosen] = useState<ActionButton | null>(initialAction);
    const [comment, setComment] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [touched, setTouched] = useState(false);
    const commentId = useId();
    const buttons = actionButtons(request);

    // The request changed underneath (answer of an action, reload): a chosen action that is no longer allowed goes.
    useEffect(() => {
        if (chosen && !buttons.some(b => b.action === chosen.action && b.onBehalf === chosen.onBehalf)) setChosen(null);
    }, [buttons, chosen]);

    const choose = (b: ActionButton) => { setChosen(b); setComment(''); setError(null); setTouched(false); };
    const invalid = chosen ? commentError(chosen, comment) : null;

    const send = async () => {
        if (!chosen) return;
        setTouched(true);
        if (invalid) return;
        setBusy(true);
        setError(null);
        try {
            await onAct(request.id, { action: chosen.action, comment: chosen.noComment || comment.trim() === '' ? null : comment.trim(), onBehalf: chosen.onBehalf });
            setChosen(null);
            setComment('');
        } catch (err) {
            setError(apiErrorText(err, 'The action failed.'));
        } finally {
            setBusy(false);
        }
    };

    const footer = chosen ? (
        <>
            <button type="button" className="btn secondary sm" onClick={() => setChosen(null)} disabled={busy}>Cancel</button>
            <button type="button" className="btn primary sm" onClick={send} disabled={busy}>{busy ? (chosen.action === 'nav_write' || chosen.action === 'nav_check' ? 'Talking to NAV…' : 'Sending…') : `Confirm: ${chosen.label}`}</button>
        </>
    ) : buttons.length > 0 ? (
        <>
            {buttons.map(b => (
                <button key={`${b.action}-${b.onBehalf}`} type="button" className={`btn ${b.tone} sm`} onClick={() => choose(b)}>{b.label}</button>
            ))}
        </>
    ) : <span className="muted">No action open to you.</span>;

    return (
        <OverviewDialog
            title={`Request #${request.id}`}
            subtitle={`${request.customerName ?? request.customerCode} (${request.customerCode})`}
            onClose={onClose}
            footer={footer}
            width={760}
        >
            <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 16px', margin: 0 }}>
                <dt className="muted">Limit CHF</dt>
                <dd style={{ margin: 0 }} className="tnum">{formatChfExact(request.currentLimitChf)} → <b>{formatChfExact(request.requestedLimitChf)}</b></dd>
                <dt className="muted">Tier</dt>
                <dd style={{ margin: 0 }}>{request.tierNo}</dd>
                <dt className="muted">Status</dt>
                <dd style={{ margin: 0 }}>{statusText(request)}</dd>
                <dt className="muted">Requested</dt>
                <dd style={{ margin: 0 }} title={request.requestedBy}>{nameFromEmail(request.requestedBy)}, {formatDateTime(request.requestedAt)}</dd>
                <dt className="muted">Reason</dt>
                <dd style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{request.reason}</dd>
            </dl>

            <h3 style={{ margin: '16px 0 6px', fontSize: 'var(--fs-body)', fontWeight: 640, color: 'var(--text-strong)' }}>Approval steps</h3>
            <ol style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 4 }}>
                {request.steps.map(s => (
                    <li key={s.stepNo}>
                        <span title={s.approverEmail}>{nameFromEmail(s.approverEmail)}</span> ({ROLE_LABEL[s.role]}): <b>{DECISION[s.decision] ?? s.decision}</b>
                        {s.decidedBy && s.decision !== 'pending' ? <span className="muted">{` by ${nameFromEmail(s.decidedBy)}${s.onBehalf ? ' on behalf' : ''}, ${formatDateTime(s.decidedAt)}`}</span> : null}
                        {s.comment ? <span className="muted">{` – «${s.comment}»`}</span> : null}
                    </li>
                ))}
            </ol>

            {request.events.length > 0 && (
                <>
                    <h3 style={{ margin: '16px 0 6px', fontSize: 'var(--fs-body)', fontWeight: 640, color: 'var(--text-strong)' }}>History</h3>
                    <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 2 }}>
                        {request.events.map(e => (
                            <li key={e.id}>
                                <span className="muted">{formatDateTime(e.createdAt)}</span> {EVENT[e.type] ?? e.type} · <span title={e.actorEmail}>{nameFromEmail(e.actorEmail)}</span>
                                {e.comment ? <span className="muted">{` – «${e.comment}»`}</span> : null}
                            </li>
                        ))}
                    </ul>
                </>
            )}

            <NavBlock request={request} isFinance={isFinance} />

            {chosen && (
                <div style={{ marginTop: 16, display: 'grid', gap: 6 }}>
                    {chosen.action === 'nav_write' && (
                        <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', margin: 0 }} aria-label="NAV write">
                            <dt className="muted">Customer</dt>
                            <dd style={{ margin: 0 }}>{request.customerName ?? request.customerCode} ({request.customerCode})</dd>
                            <dt className="muted">Old limit CHF</dt>
                            <dd style={{ margin: 0 }} className="tnum">{formatChfExact(request.currentLimitChf)}</dd>
                            <dt className="muted">New limit CHF</dt>
                            <dd style={{ margin: 0 }} className="tnum"><b>{formatChfExact(request.requestedLimitChf)}</b></dd>
                        </dl>
                    )}
                    <p style={{ margin: 0 }}>{chosen.explain}</p>
                    {chosen.action === 'nav_write' && navLocked && (
                        <div className="banner attention" role="status"><p>NAV writing is locked. The write will be refused until Finance unlocks it in the Approval rules tab.</p></div>
                    )}
                    {!chosen.noComment && <label htmlFor={commentId} className="muted">{chosen.commentRequired ? 'Reason (required)' : 'Comment (optional)'}</label>}
                    {!chosen.noComment && <textarea
                        id={commentId}
                        className="input"
                        style={{ height: 'auto', minHeight: 80, padding: '8px 12px' }}
                        rows={3}
                        maxLength={MAX_COMMENT_LENGTH}
                        value={comment}
                        aria-required={chosen.commentRequired}
                        aria-invalid={touched && invalid !== null}
                        onChange={e => setComment(e.target.value)}
                    />}
                    {touched && invalid && <p className="hint" role="alert" style={{ margin: 0 }}>{invalid}</p>}
                </div>
            )}
            {error && <div className="banner error" role="alert" style={{ marginTop: 12 }}><p>{error}</p></div>}
        </OverviewDialog>
    );
}
